import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CsvImporterView, CsvImporterViewProps } from './CsvImporter.view';
import { format } from 'date-fns';
import { CsvEvergreen } from '../../../constants/csv-importer';
import { CsvImportedPost, ImportResults } from '../../../models/csv-importer';
import updateImmutable from 'immutability-helper';
import { InitialPostEditorState } from '../../PostEditor/states/initial';
import { PostEditorContextEnum, PostGroupsType, ScheduledDateTimesType } from '../../../models/post-editor';
import { AppContext } from '../../../providers/AppProvider';
import { getPostEditorMediaTypeFromUrl } from '../../PostEditor/helpers/getPostEditorMediaTypeFromUrl';
import { PostEditorMediaAddFromExternalDeferredAction } from '../../PostEditor/states/PostEditorActions';
import { DateTimeStringFormat } from '../../../constants/time-formats';
import { usePostLinkSettings } from '../../../hooks/usePostLinkSettings';
import { triggerErrorToast } from '../../../helpers/triggerErrorToast';
import { usePostEditorWorkspaceSettings } from '../../../hooks/usePostEditorWorkspaceSettings';
import { showToast } from '../../../helpers/showToast';
import { CoreCategoryFieldsFragment, PostSource, PostTiming, ThirdPartyPost } from '../../../models/graphql';
import { mapCsvImportedPostsToThirdPartyPost } from '../../../helpers/csvImporterHelpers';
import { mapThirdPartyPostToState } from '../../PostEditor/helpers/mapThirdPartyPostToState';
import { CustomParagraph } from '../../../models/slate-editor';
import { deserializeText } from '../../PostEditor/TextEditor/helpers/serializer';
import { useCsvImporterValidator } from '../../../hooks/useCsvImporterValidator';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';

export type CsvImporterControllerProps = {
  categories: CoreCategoryFieldsFragment[];
  loading: boolean;
};

export const CsvImporterController = ({ categories, loading }: CsvImporterControllerProps): JSX.Element => {
  const { setPostEditorContext, setSuspendedModalOpen } = useContext(AppContext);
  const { isTemporallySuspended } = useSubscriptionStatus();
  const { errorState, validateData, validateFile } = useCsvImporterValidator();

  const { loading: linkSettingsLoading, error: linkSettingsError, ...postLinkSettings } = usePostLinkSettings();
  const workspaceSettings = usePostEditorWorkspaceSettings();

  const [zoneHover, setZoneHover] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const processedPosts = useRef<CsvImportedPost[]>([]);

  const sendToBulkPostEditor = useCallback(
    (posts: CsvImportedPost[]) => {
      if (!categories || !workspaceSettings) {
        showToast({
          severity: 'error',
          summary: 'Processing errror',
          sticky: true,
          detail: 'There was an error trying to process your CSV import'
        });
        return;
      }
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      let initialState = InitialPostEditorState();

      const externalMediaArrays = posts.map(post => getPostEditorMediaTypeFromUrl(post.images));
      const payload: PostEditorMediaAddFromExternalDeferredAction['payload'] = {
        externalMedia: externalMediaArrays
      };
      const thirdPartyPosts: ThirdPartyPost[] = mapCsvImportedPostsToThirdPartyPost(posts);
      const postGroups = mapThirdPartyPostToState(thirdPartyPosts, postLinkSettings, PostSource.CSV_BULK);

      const updatedPostGroups = postGroups.map((group, index) =>
        updateImmutable<PostGroupsType>(group, {
          categoryId: {
            $set: categories.find(c => c.name === posts[index].category)?.id || null
          },
          groupPostAttributes: {
            scheduling: {
              evergreen: {
                active: {
                  $set:
                    posts[index].evergreen.toLowerCase() === CsvEvergreen.ONCE
                      ? false
                      : posts[index].evergreen.toLowerCase() === CsvEvergreen.EVERGREEN
                      ? true
                      : workspaceSettings.evergreen
                }
              },
              queuePosition: {
                $set: workspaceSettings.queuePosition
              },
              scheduledTimezone: {
                $set: workspaceSettings.scheduledTimezone
              },
              postTiming: {
                $apply: current => (posts[index].scheduledTime ? PostTiming.SCHEDULE : current)
              },
              scheduledLocalDateTimes: {
                $apply: (current: ScheduledDateTimesType[]) =>
                  posts[index].scheduledTime
                    ? [
                        {
                          dateTime: format(new Date(posts[index].scheduledTime), DateTimeStringFormat),
                          isPast: false
                        }
                      ]
                    : current
              }
            },
            content: {
              title: {
                $set: posts[index].title
              },
              firstComment: {
                $apply: (current: CustomParagraph[]) =>
                  posts[index].firstComment ? deserializeText(posts[index].firstComment, [], null, false) : current
              },
              shouldShowFirstComment: {
                $set: !!posts[index].firstComment
              },
              varyMedia: {
                $set: workspaceSettings.varyMedia
              }
            }
          }
        })
      );

      initialState = updateImmutable(initialState, {
        postGroups: {
          $set: updatedPostGroups
        }
      });

      setPostEditorContext({
        type: PostEditorContextEnum.import,
        overrideWorkspaceSettings: true,
        initialState,
        payload: { type: 'mediaAddFromExternalDeferred', payload }
      });

      processedPosts.current = posts;
    },
    [categories, postLinkSettings, setPostEditorContext, workspaceSettings, isTemporallySuspended, setSuspendedModalOpen]
  );

  // If the Headers are not valid or if not posts data rows, we do not continue
  const validateImportedPost = useCallback(
    (results: ImportResults): void => {
      const { posts, hasErrors } = validateData(results, categories);

      // Only process if we have posts and no errors
      if (!hasErrors && posts.length) {
        sendToBulkPostEditor(posts);
      } else {
        processedPosts.current = posts;
      }

      setZoneHover(false);
      setProcessing(false);
    },
    [categories, sendToBulkPostEditor, validateData]
  );

  useEffect(() => {
    if (linkSettingsError) {
      triggerErrorToast('Error getting link settings')(linkSettingsError);
    }
  }, [linkSettingsError]);

  const viewProps: CsvImporterViewProps = {
    loading: loading || linkSettingsLoading,
    zoneHover,
    setZoneHover,
    validateFile,
    validationHandler: validateImportedPost,
    errors: Object.keys(errorState.errorMessages),
    validationErrorDetails: errorState.errorDetails,
    processing,
    setProcessing,
    posts: processedPosts.current
  };

  return <CsvImporterView {...viewProps} />;
};
